import React, { useState, useEffect, useCallback } from 'react';
import ReactQuill from 'react-quill-new';
import { Button } from '../ui/button';
import { X, Moon, Sun, Type, Minus, Plus, Save, ChevronLeft, ChevronRight } from 'lucide-react';
import 'react-quill-new/dist/quill.snow.css';

const CompositionMode = ({ 
    isOpen, 
    onClose, 
    content, 
    onContentChange,
    chapterTitle,
    wordCount,
    totalWordCount,
    onSave,
    chapters,
    currentChapterIndex,
    onNavigateChapter
}) => {
    const [theme, setTheme] = useState(() => localStorage.getItem('composition-theme') || 'dark');
    const [fontSize, setFontSize] = useState(() => parseInt(localStorage.getItem('composition-font-size')) || 18);
    const [lineWidth, setLineWidth] = useState(() => localStorage.getItem('composition-width') || 'medium');
    const [showControls, setShowControls] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    // Save preferences
    useEffect(() => {
        localStorage.setItem('composition-theme', theme);
        localStorage.setItem('composition-font-size', fontSize.toString());
        localStorage.setItem('composition-width', lineWidth);
    }, [theme, fontSize, lineWidth]);

    // Auto-hide controls after inactivity
    useEffect(() => {
        let timeout;
        const handleMouseMove = () => {
            setShowControls(true);
            clearTimeout(timeout);
            timeout = setTimeout(() => setShowControls(false), 3000);
        };

        if (isOpen) {
            window.addEventListener('mousemove', handleMouseMove);
            timeout = setTimeout(() => setShowControls(false), 3000);
        }

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            clearTimeout(timeout);
        };
    }, [isOpen]);

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!isOpen) return;
            
            // Escape to exit
            if (e.key === 'Escape') {
                onClose();
            }
            // Ctrl/Cmd + S to save
            if ((e.ctrlKey || e.metaKey) && e.key === 's') {
                e.preventDefault();
                handleSave();
            }
            // Ctrl/Cmd + Arrow to navigate chapters
            if ((e.ctrlKey || e.metaKey) && e.key === 'ArrowLeft') {
                e.preventDefault();
                navigatePrev();
            }
            if ((e.ctrlKey || e.metaKey) && e.key === 'ArrowRight') {
                e.preventDefault();
                navigateNext();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, currentChapterIndex]);

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await onSave();
        } finally {
            setIsSaving(false);
        }
    };

    const navigatePrev = () => {
        if (currentChapterIndex > 0) {
            onNavigateChapter(currentChapterIndex - 1);
        }
    };

    const navigateNext = () => {
        if (currentChapterIndex < chapters.length - 1) {
            onNavigateChapter(currentChapterIndex + 1);
        }
    };

    const widthClasses = {
        narrow: 'max-w-xl',
        medium: 'max-w-2xl',
        wide: 'max-w-4xl',
        full: 'max-w-5xl'
    };

    const themeStyles = {
        dark: {
            bg: 'bg-zinc-900',
            text: 'text-zinc-100',
            accent: 'text-amber-400',
            muted: 'text-zinc-500',
            editorBg: '#18181b',
            editorText: '#fafafa'
        },
        sepia: {
            bg: 'bg-amber-50',
            text: 'text-amber-950',
            accent: 'text-amber-700',
            muted: 'text-amber-700/60',
            editorBg: '#fffbeb',
            editorText: '#451a03'
        },
        light: {
            bg: 'bg-white',
            text: 'text-gray-900',
            accent: 'text-blue-600',
            muted: 'text-gray-500',
            editorBg: '#ffffff',
            editorText: '#111827'
        }
    };

    const currentTheme = themeStyles[theme];

    const quillModules = {
        toolbar: false // No toolbar in composition mode
    };

    if (!isOpen) return null;

    return (
        <div className={`fixed inset-0 z-50 ${currentTheme.bg} ${currentTheme.text} transition-colors duration-300`}>
            {/* Top Controls */}
            <div className={`
                absolute top-0 left-0 right-0 p-4 flex items-center justify-between
                transition-opacity duration-300
                ${showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'}
            `}>
                <div className="flex items-center gap-4">
                    <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={onClose}
                        className={`${currentTheme.muted} hover:${currentTheme.text}`}
                    >
                        <X className="h-4 w-4 mr-2" />
                        Exit Focus Mode
                    </Button>
                    <span className={`text-sm ${currentTheme.muted}`}>
                        Press ESC to exit
                    </span>
                </div>

                <div className="flex items-center gap-6">
                    {/* Word Counts */}
                    <div className="flex items-center gap-4 text-sm">
                        <span className={currentTheme.muted}>
                            Chapter: <span className={currentTheme.accent}>{wordCount?.toLocaleString() || 0}</span> words
                        </span>
                        <span className={currentTheme.muted}>
                            Total: <span className={currentTheme.accent}>{totalWordCount?.toLocaleString() || 0}</span> words
                        </span>
                    </div>

                    {/* Save Button */}
                    <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={handleSave}
                        disabled={isSaving}
                        className={currentTheme.muted}
                    >
                        <Save className="h-4 w-4 mr-1" />
                        {isSaving ? 'Saving...' : 'Save'}
                    </Button>
                </div>
            </div>

            {/* Chapter Title & Navigation */}
            <div className={`
                absolute top-16 left-0 right-0 text-center
                transition-opacity duration-300
                ${showControls ? 'opacity-100' : 'opacity-0'}
            `}>
                <div className="flex items-center justify-center gap-4">
                    <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={navigatePrev}
                        disabled={currentChapterIndex <= 0}
                        className={currentTheme.muted}
                    >
                        <ChevronLeft className="h-5 w-5" />
                    </Button>
                    <h1 className={`font-serif text-xl ${currentTheme.accent}`}>
                        {chapterTitle}
                    </h1>
                    <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={navigateNext}
                        disabled={currentChapterIndex >= chapters.length - 1}
                        className={currentTheme.muted}
                    >
                        <ChevronRight className="h-5 w-5" />
                    </Button>
                </div>
            </div>

            {/* Main Editor */}
            <div className={`h-full pt-28 pb-20 px-4 overflow-y-auto ${widthClasses[lineWidth]} mx-auto`}>
                <style>{`
                    .composition-editor .ql-container {
                        font-size: ${fontSize}px;
                        font-family: 'EB Garamond', Georgia, serif;
                    }
                    .composition-editor .ql-editor {
                        background: transparent !important;
                        color: ${currentTheme.editorText} !important;
                        line-height: 1.8;
                        padding: 2rem 0;
                        min-height: calc(100vh - 200px);
                    }
                    .composition-editor .ql-editor::before {
                        color: ${theme === 'dark' ? '#52525b' : '#9ca3af'} !important;
                        font-style: italic;
                    }
                    .composition-editor .ql-editor p {
                        margin-bottom: 1.5rem;
                    }
                `}</style>
                <div className="composition-editor">
                    <ReactQuill
                        theme="snow"
                        value={content}
                        onChange={onContentChange}
                        modules={quillModules}
                        placeholder="Begin writing..."
                    />
                </div>
            </div>

            {/* Bottom Controls */}
            <div className={`
                absolute bottom-0 left-0 right-0 p-4 flex items-center justify-center gap-8
                transition-opacity duration-300
                ${showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'}
            `}>
                {/* Theme Selector */}
                <div className="flex items-center gap-2">
                    <span className={`text-xs ${currentTheme.muted}`}>Theme:</span>
                    <div className="flex rounded-lg overflow-hidden border border-current/20">
                        <button
                            className={`px-3 py-1.5 text-xs ${theme === 'dark' ? 'bg-white/10' : ''}`}
                            onClick={() => setTheme('dark')}
                            title="Dark"
                        >
                            <Moon className="h-4 w-4" />
                        </button>
                        <button
                            className={`px-3 py-1.5 text-xs ${theme === 'sepia' ? 'bg-white/10' : ''}`}
                            onClick={() => setTheme('sepia')}
                            title="Sepia"
                        >
                            <span className="text-amber-600">☀</span>
                        </button>
                        <button
                            className={`px-3 py-1.5 text-xs ${theme === 'light' ? 'bg-black/10' : ''}`}
                            onClick={() => setTheme('light')}
                            title="Light"
                        >
                            <Sun className="h-4 w-4" />
                        </button>
                    </div>
                </div>

                {/* Font Size */}
                <div className="flex items-center gap-2">
                    <span className={`text-xs ${currentTheme.muted}`}>Size:</span>
                    <div className="flex items-center gap-1">
                        <button
                            className={`p-1.5 rounded hover:bg-white/10`}
                            onClick={() => setFontSize(Math.max(14, fontSize - 2))}
                        >
                            <Minus className="h-3 w-3" />
                        </button>
                        <span className="text-sm w-8 text-center">{fontSize}</span>
                        <button
                            className={`p-1.5 rounded hover:bg-white/10`}
                            onClick={() => setFontSize(Math.min(28, fontSize + 2))}
                        >
                            <Plus className="h-3 w-3" />
                        </button>
                    </div>
                </div>

                {/* Line Width */}
                <div className="flex items-center gap-2">
                    <span className={`text-xs ${currentTheme.muted}`}>Width:</span>
                    <div className="flex rounded-lg overflow-hidden border border-current/20">
                        {['narrow', 'medium', 'wide', 'full'].map(w => (
                            <button
                                key={w}
                                className={`px-2 py-1.5 text-xs capitalize ${lineWidth === w ? 'bg-white/10' : ''}`}
                                onClick={() => setLineWidth(w)}
                            >
                                {w}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Keyboard Shortcuts Hint */}
            <div className={`
                absolute bottom-16 left-0 right-0 text-center
                transition-opacity duration-500
                ${showControls ? 'opacity-50' : 'opacity-0'}
            `}>
                <span className={`text-xs ${currentTheme.muted}`}>
                    Ctrl+S Save • Ctrl+← Prev Chapter • Ctrl+→ Next Chapter
                </span>
            </div>
        </div>
    );
};

export default CompositionMode;
